import { useState, type FormEvent } from "react";
import { Link, Navigate, useLocation, useNavigate } from "react-router-dom";
import { loginRequestSchema } from "@vmeste/shared";
import { AuthShell } from "../components/AuthShell";
import { Button, PasswordField, TextField } from "../components/ui";
import { QrScanner } from "../components/QrScanner";
import { QrIcon } from "../components/icons";
import { useSession } from "../app/session";
import { joinFromScan } from "../lib/encryption";
import { validate } from "../lib/form";
import { useT } from "../i18n/index.js";

export function SignInPage() {
  const { session, signIn, pending, error } = useSession();
  const t = useT();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [scanning, setScanning] = useState(false);
  const navigate = useNavigate();

  // Back to whatever they were opening, if anything sent them here.
  const from = (location.state as { from?: { pathname: string; search: string } } | null)
    ?.from;
  const destination = from ? `${from.pathname}${from.search}` : "/lists";

  if (session) return <Navigate to={destination} replace />;

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    // Built here rather than at module scope: a map made once on import would
    // keep the language it was loaded in after the person switches.
    const result = validate(loginRequestSchema, { email, password }, {
      email: t("auth.emailInvalid"),
      password: t("auth.passwordRequired"),
    });
    if (!result.ok) {
      setErrors(result.errors);
      return;
    }
    setErrors({});
    await signIn(result.value);
  }

  return (
    <AuthShell
      title={t("auth.signInTitle")}
      lede={t("auth.signInLede")}
      footer={
        <>
          {t("auth.haveInvite")}{" "}
          {/* Куда человек шёл, туда он и попадёт — регистрация читает то же
              состояние, что и вход. Без этого ссылка, приведшая на вход,
              теряется у того единственного, кто ещё не заводил учётной записи. */}
          <Link
            to="/register"
            state={location.state}
            className="text-accent underline underline-offset-4"
          >
            {t("auth.registerLink")}
          </Link>
        </>
      }
    >
      <form className="flex flex-col gap-4" onSubmit={onSubmit} noValidate>
        <TextField
          label={t("auth.email")}
          type="email"
          name="email"
          autoComplete="username"
          inputMode="email"
          autoCapitalize="none"
          autoCorrect="off"
          placeholder="you@example.com"
          value={email}
          error={errors["email"]}
          onChange={(e) => setEmail(e.target.value)}
        />
        <PasswordField
          label={t("auth.password")}
          showLabel={t("auth.showPassword")}
          hideLabel={t("auth.hidePassword")}
          name="password"
          autoComplete="current-password"
          value={password}
          error={errors["password"]}
          onChange={(e) => setPassword(e.target.value)}
        />
        {error ? (
          <p role="alert" className="rounded-card bg-neg-soft px-3 py-2 text-[0.875rem] text-neg">
            {error}
          </p>
        ) : null}

        <Button type="submit" full disabled={pending}>
          {pending ? t("auth.signingIn") : t("auth.signIn")}
        </Button>
      </form>

      {/*
        Квадрат работает и здесь.
        
        Наведённая на него системная камера открывает ссылку сама — но она есть
        не всегда и не у всех под рукой, а приложение в этот момент уже открыто
        на нужном экране. Отсюда дорога та же: `/join` решает, впускать ли, и
        что делать с ключом, — здесь только читают квадрат.
      */}
      <div className="mt-6 flex flex-col gap-3">
        {scanning ? (
          <QrScanner
            onFound={(text) => {
              const target = joinFromScan(text);
              if (!target) return false;

              // Целиком, вместе с фрагментом: ключ живёт после решётки, и
              // потерять его здесь значило бы отправить человека на экран,
              // которому нечего применить.
              navigate(`/join?space=${target.spaceId}${
                target.invite ? `&invite=${encodeURIComponent(target.invite)}` : ""
              }#k=${target.material}`);
              return true;
            }}
            onCancel={() => setScanning(false)}
          />
        ) : (
          <Button variant="secondary" full onClick={() => setScanning(true)}>
            <QrIcon className="size-[18px]" strokeWidth={1.9} />
            {t("auth.scanInvite")}
          </Button>
        )}
      </div>
    </AuthShell>
  );
}
